import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, Button, Card, message, Progress, Table, 
  Typography, Space, Alert, Divider, Tag, Modal
} from 'antd';
import { 
  InboxOutlined, FileAddOutlined, FileExcelOutlined,
  DeleteOutlined, CheckCircleOutlined, FileTextOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import Papa from 'papaparse';
import dayjs from '../../../dayjs';
import OTRDataService from '../utils/OTRDataService';

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

// Default KPI threshold values (in minutes)
const DEFAULT_KPI_THRESHOLDS = {
  earlyThreshold: -2,   // More than 2 minutes early is considered EARLY
  lateThreshold: 5,     // More than 5 minutes late is considered LATE
  veryLateThreshold: 10  // More than 10 minutes late is considered VERY LATE (for UI highlighting)
};

// Debugging function to safely log data issues
const debugLog = (message, data) => {
  console.log(message, data ? JSON.stringify(data).substring(0, 1000) : '');
};

const OTRImporter = ({ onImportComplete, kpiThresholds }) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [importedData, setImportedData] = useState(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [importStats, setImportStats] = useState({
    totalFiles: 0,
    processedFiles: 0,
    totalRows: 0,
    successRows: 0,
    errorRows: 0
  });
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [fileDebugInfo, setFileDebugInfo] = useState(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  
  // Use provided KPI thresholds or defaults
  const thresholds = kpiThresholds || DEFAULT_KPI_THRESHOLDS;
  
  // Add a ref to track component mounted state
  const isMounted = useRef(true);
  
  // Set up cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fileProps = {
    name: 'file',
    multiple: true,
    accept: '.csv',
    beforeUpload: (file) => {
      // Check if file is CSV
      const isCsv = file.type === 'text/csv' || file.name.endsWith('.csv');
      if (!isCsv) {
        message.error(`${file.name} is not a CSV file`);
        return Upload.LIST_IGNORE;
      }
      
      // Add to fileList
      setFileList(prevList => [...prevList, file]);
      return false; // Prevent auto upload
    },
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    fileList,
  };
  
  
  const handleImport = async () => {
    if (fileList.length === 0) {
      message.error('Please select at least one CSV file to import');
      return;
    }
    
    setUploading(true);
    setImportStats({
      totalFiles: fileList.length,
      processedFiles: 0,
      totalRows: 0,
      successRows: 0,
      errorRows: 0
    });
    
    const allData = [];
    let successFiles = 0;
    let totalRows = 0;
    let successRows = 0;
    let errorRows = 0;
    
    try {
      for (let i = 0; i < fileList.length; i++) {
        // Check if component is still mounted before updating state
        if (!isMounted.current) return;
        
        setCurrentFileIndex(i);
        setCurrentProgress(0);
        
        const file = fileList[i];
        
        try {
          const result = await OTRDataService.processFile(file);
          
          // Check if component is still mounted before updating state
          if (!isMounted.current) return;
          
          // Add to all data
          allData.push(result);
          
          // Update stats
          successFiles++;
          totalRows += result.totalRows;
          successRows += result.processedRows;
          errorRows += result.errorRows;
          
          setImportStats(prev => ({
            ...prev,
            processedFiles: successFiles,
            totalRows: totalRows,
            successRows: successRows,
            errorRows: errorRows
          }));
          
          // Show success for this file
          message.success(`Processed ${file.name} successfully (${result.processedRows} rows)`);
        } catch (error) {
          // Check if component is still mounted before updating state
          if (!isMounted.current) return;
          
          console.error(`Error processing file ${file.name}:`, error);
          message.error(`Failed to process ${file.name}: ${error.message}`);
          
          // Store available fields if any
          if (error.availableFields) {
            console.log(`Available fields in ${file.name}:`, error.availableFields);
          }
          
          // Still count as processed
          setImportStats(prev => ({
            ...prev,
            processedFiles: prev.processedFiles + 1,
            errorRows: prev.errorRows + 1
          }));
        }
      }
      
      // Check if component is still mounted before updating state
      if (!isMounted.current) return;
      
      // Combine all data into a single dataset
      const combinedData = allData.reduce((acc, curr) => {
        return acc.concat(curr.data);
      }, []);
      
      // Process combined data to ensure consistent data types (e.g., routes as strings)
      const processedCombinedData = OTRDataService.processNormalizedData(combinedData);
      
      // Set imported data
      setImportedData({
        files: allData,
        combinedData: processedCombinedData
      });
      
      // Set preview data for modal
      if (processedCombinedData.length > 0) {
        setPreviewData(processedCombinedData.slice(0, 100)); // First 100 rows for preview
      }
      
      // Notify completion
      if (successFiles === fileList.length) {
        message.success(`Successfully imported all ${fileList.length} files`);
      } else {
        message.warning(`Imported ${successFiles} out of ${fileList.length} files`);
      }
      
      // Callback with the imported data
      if (onImportComplete && processedCombinedData.length > 0) {
        onImportComplete(processedCombinedData);
      }
    } catch (error) {
      // Check if component is still mounted before updating state
      if (!isMounted.current) return;
      
      console.error('Import error:', error);
      message.error(`Import failed: ${error.message}`);
    } finally {
      // Check if component is still mounted before updating state
      if (isMounted.current) {
        setUploading(false);
      }
    }
  };

  const showDataPreview = () => {
    if (!importedData || importedData.combinedData.length === 0) {
      message.info('No data available to preview');
      return;
    }
    
    setShowPreview(true);
  };
  
  const toggleDebugInfo = () => {
    setShowDebugInfo(!showDebugInfo);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'DATE_ID',
      key: 'DATE_ID',
    },
    {
      title: 'Route',
      dataIndex: 'ROUTE',
      key: 'ROUTE',
    },
    {
      title: 'Trip ID',
      dataIndex: 'TRIP_ID',
      key: 'TRIP_ID',
    },
    {
      title: 'Scheduled Arr',
      dataIndex: 'SCHEDULED_ARRIVAL_TIME',
      key: 'SCHEDULED_ARRIVAL_TIME',
    },
    {
      title: 'Actual Arr',
      dataIndex: 'ACTUAL_ARRIVAL_TIME',
      key: 'ACTUAL_ARRIVAL_TIME',
    },
    {
      title: 'Scheduled Dep',
      dataIndex: 'SCHEDULED_DEPARTURE_TIME',
      key: 'SCHEDULED_DEPARTURE_TIME',
    },
    {
      title: 'Actual Dep',
      dataIndex: 'ACTUAL_DEPARTURE_TIME',
      key: 'ACTUAL_DEPARTURE_TIME',
    },
    {
      title: 'Dep Variance',
      dataIndex: 'DEPARTURE_TIME_VARIANCE',
      key: 'DEPARTURE_TIME_VARIANCE',
      render: val => (
        <Text type={val > thresholds.veryLateThreshold ? 'danger' : val < thresholds.earlyThreshold ? 'warning' : 'success'}>
          {val !== null && val !== undefined ? `${val} min` : '-'}
        </Text>
      )
    },
    {
      title: 'Arr Variance',
      dataIndex: 'ARRIVAL_TIME_VARIANCE',
      key: 'ARRIVAL_TIME_VARIANCE',
      render: val => (
        <Text type={val > thresholds.veryLateThreshold ? 'danger' : val < thresholds.earlyThreshold ? 'warning' : 'success'}>
          {val !== null && val !== undefined ? `${val} min` : '-'}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'ON_TIME',
      key: 'ON_TIME',
      render: status => {
        if (!status) return <Tag>Unknown</Tag>;
        switch (status) {
          case 'ON_TIME': return <Tag color="green">On Time</Tag>;
          case 'EARLY': return <Tag color="orange">Early</Tag>;
          case 'LATE': return <Tag color="red">Late</Tag>;
          default: return <Tag>Unknown</Tag>;
        }
      }
    },
    {
      title: 'Stop Type',
      dataIndex: 'TSN_FIRST_LAST_IND',
      key: 'TSN_FIRST_LAST_IND',
      render: (val, record) => {
        const stopType = record.STOP_TYPE || 'UNKNOWN';
        switch (stopType) {
          case 'FIRST': return <Tag color="blue">First</Tag>;
          case 'MID': return <Tag color="purple">Mid</Tag>;
          case 'LAST': return <Tag color="geekblue">Last</Tag>;
          default: return <Tag>Unknown</Tag>;
        }
      }
    },
    {
      title: 'Depot',
      dataIndex: 'DEPOT',
      key: 'DEPOT',
    },
    {
      title: 'Driver',
      dataIndex: 'DRIVER_ID',
      key: 'DRIVER_ID',
    }
  ];

  return (
    <div>
      <Card title="TPT Data Importer">
        <Paragraph>
          Import Trip Passing Time (TPT) data files for On-Time Running analysis. 
          These files typically contain stop-level timing data for trips.
        </Paragraph>
        
        <Alert 
          message="File Format Requirements" 
          description={
            <ul>
              <li>CSV files with semicolon (;), comma (,), or tab delimiters</li>
              <li>Required fields: DATE_ID, ROUTE, TRIP_ID, SCHEDULED_ARRIVAL_TIME, ACTUAL_ARRIVAL_TIME, ARRIVAL_TIME_VARIANCE, IS_TIMING_POINT, TSN_FIRST_LAST_IND, DRIVER_ID, DEPOT, TSN</li>
              <li>Each file should represent a single day of operations</li>
              <li>Multiple files can be imported at once for analysis across dates</li>
            </ul>
          }
          type="info" 
          showIcon 
          style={{ marginBottom: '20px' }}
        />
        
        <Dragger {...fileProps} disabled={uploading}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag TPT CSV files to this area</p>
          <p className="ant-upload-hint">
            Support for single or bulk upload of CSV files containing Trip Passing Time data.
          </p>
        </Dragger>
        
        <div style={{ marginTop: '20px' }}>
          <Space>
            <Button 
              type="primary" 
              onClick={handleImport} 
              loading={uploading}
              icon={<FileExcelOutlined />}
              disabled={fileList.length === 0}
            >
              {uploading ? 'Importing...' : 'Import Selected Files'}
            </Button>
            
            <Button 
              type="default" 
              onClick={showDataPreview} 
              icon={<FileTextOutlined />}
              disabled={!importedData || !importedData.combinedData || importedData.combinedData.length === 0}
            >
              Preview Data
            </Button>

            <Button
              type="default"
              onClick={toggleDebugInfo}
              icon={<InfoCircleOutlined />}
              disabled={!fileDebugInfo}
            >
              {showDebugInfo ? 'Hide File Info' : 'Show File Info'}
            </Button>
          </Space>
        </div>
        
        {showDebugInfo && fileDebugInfo && (
          <div style={{ marginTop: '20px' }}>
            <Alert
              message="File Debug Information"
              description={
                <div>
                  <p><strong>File:</strong> {fileDebugInfo.fileName}</p>
                  <p><strong>Size:</strong> {fileDebugInfo.fileSize} bytes</p>
                  <p><strong>Has Content:</strong> {fileDebugInfo.hasContent ? 'Yes' : 'No'}</p>
                  <p><strong>Content Length:</strong> {fileDebugInfo.contentLength} characters</p>
                  <p><strong>Line Count:</strong> {fileDebugInfo.lineCount} lines</p>
                  <p><strong>Sample Content:</strong></p>
                  <pre style={{ 
                    background: '#f6f6f6', 
                    padding: '10px', 
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {fileDebugInfo.sampleContent}
                  </pre>
                </div>
              }
              type="info"
              showIcon
            />
          </div>
        )}
        
        {uploading && (
          <div style={{ marginTop: '20px' }}>
            <Title level={5}>
              Processing file {currentFileIndex + 1} of {fileList.length}: {fileList[currentFileIndex]?.name}
            </Title>
            <Progress percent={currentProgress} status="active" />
          </div>
        )}
        
        {importedData && (
          <div style={{ marginTop: '20px' }}>
            <Divider />
            <Title level={4}>Import Summary</Title>
            <Space size="large" style={{ marginBottom: '20px' }}>
              <Statistic title="Files Processed" value={importStats.processedFiles} suffix={`/ ${importStats.totalFiles}`} />
              <Statistic title="Total Rows" value={importStats.totalRows} />
              <Statistic title="Successful Rows" value={importStats.successRows} prefix={<CheckCircleOutlined style={{ color: 'green' }} />} />
              <Statistic title="Error Rows" value={importStats.errorRows} prefix={<DeleteOutlined style={{ color: 'red' }} />} />
            </Space>
            
            {importedData.files.map((file, index) => (
              <Alert
                key={index}
                message={file.fileName}
                description={`Imported ${file.processedRows} of ${file.totalRows} rows successfully`}
                type={file.errorRows > 0 ? "warning" : "success"}
                showIcon
                style={{ marginBottom: '10px' }}
              />
            ))}
          </div>
        )}
      </Card>
      
      <Modal
        title="Data Preview"
        open={showPreview}
        onCancel={() => setShowPreview(false)}
        width={1200}
        footer={[
          <Button key="close" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        ]}
      >
        <Table 
          dataSource={previewData}
          columns={columns}
          rowKey={(record, index) => `${record.TRIP_ID}-${record.TSN}-${index}`}
          scroll={{ x: 1100, y: 500 }}
          pagination={{ pageSize: 50 }}
          size="small"
        />
      </Modal>
    </div>
  );
};

// Helper component for showing statistics
const Statistic = ({ title, value, prefix, suffix }) => (
  <div style={{ textAlign: 'center' }}>
    <Text type="secondary">{title}</Text>
    <div style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '8px' }}>
      {prefix} {value} {suffix}
    </div>
  </div>
);

export default OTRImporter; 